import React from 'react';

// ---- Button ----
export function Button({ children, variant = 'primary', size = 'md', loading, ...props }) {
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    fontFamily: 'var(--font-body)', fontWeight: 500, border: 'none',
    borderRadius: 'var(--radius)', cursor: loading ? 'not-allowed' : 'pointer',
    transition: 'all .15s', opacity: loading ? .7 : 1,
  };
  const sizes = {
    sm: { padding: '7px 14px', fontSize: 13 },
    md: { padding: '10px 20px', fontSize: 14 },
    lg: { padding: '13px 28px', fontSize: 15 },
  };
  const variants = {
    primary:   { background: 'var(--accent)', color: '#fff' },
    secondary: { background: 'var(--border)', color: 'var(--ink)' },
    ghost:     { background: 'transparent', color: 'var(--ink-2)', border: '1.5px solid var(--border)' },
    danger:    { background: 'var(--danger)', color: '#fff' },
  };
  return (
    <button style={{ ...base, ...sizes[size], ...variants[variant] }} disabled={loading} {...props}>
      {loading && <span style={{ width: 14, height: 14, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin .7s linear infinite', display: 'inline-block' }} />}
      {children}
    </button>
  );
}

// ---- Card ----
export function Card({ children, style, ...props }) {
  return (
    <div style={{ background: 'var(--bg-card)', border: '1.5px solid var(--border)', borderRadius: 'var(--radius)', padding: 24, boxShadow: 'var(--shadow)', ...style }} {...props}>
      {children}
    </div>
  );
}

// ---- Badge ----
const badgeColors = {
  valid:    { bg: 'var(--accent-lt)', color: 'var(--accent)' },
  due_soon: { bg: 'var(--warn-lt)',   color: 'var(--warn)' },
  expired:  { bg: 'var(--danger-lt)', color: 'var(--danger)' },
  unknown:  { bg: 'var(--border)',    color: 'var(--ink-3)' },
  low:      { bg: 'var(--accent-lt)', color: 'var(--accent)' },
  medium:   { bg: 'var(--warn-lt)',   color: 'var(--warn)' },
  high:     { bg: 'var(--danger-lt)', color: 'var(--danger)' },
  warning:  { bg: 'var(--warn-lt)',   color: 'var(--warn)' },
  illegal:  { bg: 'var(--danger-lt)', color: 'var(--danger)' },
};

export function Badge({ status, label }) {
  const c = badgeColors[status] || badgeColors.unknown;
  return (
    <span style={{ ...c, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
      {label || status?.replace('_', ' ')}
    </span>
  );
}

// ---- Modal ----
export function Modal({ open, onClose, title, children, width = 520 }) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div className="modal-inner" onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-card)', borderRadius: 14, padding: 32, width: '100%', maxWidth: width, maxHeight: '90vh', overflowY: 'auto', animation: 'fadeUp .2s ease', boxShadow: 'var(--shadow-lg)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h3 style={{ fontSize: 20 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--ink-3)', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ---- FormField ----
export function FormField({ label, error, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      {label && <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--ink-2)' }}>{label}</label>}
      {children}
      {error && <p style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>{error}</p>}
    </div>
  );
}

// ---- Empty state ----
export function Empty({ icon, title, subtitle, action }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--ink-3)' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>{icon}</div>
      <h3 style={{ fontSize: 18, color: 'var(--ink-2)', marginBottom: 6 }}>{title}</h3>
      {subtitle && <p style={{ fontSize: 14, marginBottom: 20 }}>{subtitle}</p>}
      {action}
    </div>
  );
}

// ---- Spinner ----
export function Spinner() {
  return <div style={{ width: 28, height: 28, border: '3px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin .7s linear infinite', margin: '40px auto' }} />;
}

// ---- Stat card ----
export function StatCard({ label, value, sub, color }) {
  return (
    <Card style={{ textAlign: 'center' }}>
      <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 6, fontWeight: 500 }}>{label}</p>
      <p style={{ fontSize: 36, fontFamily: 'var(--font-serif)', fontWeight: 300, color: color || 'var(--ink)', lineHeight: 1 }}>{value}</p>
      {sub && <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>{sub}</p>}
    </Card>
  );
}